import { Avatar, Button, Col, Icon, Spinner, Text, useTheme } from "@rally/ui-kit";
import { useEffect, useState } from "react";
import type { Card } from "../chat/panel/stacking.ts";
import { say } from "../localization/say.ts";
import { calledBy } from "../me/called.ts";
import { useFace } from "../me/face.ts";
import { act, read } from "../net/rally.ts";
import { goTo, type SearchResourceAddress } from "../router.ts";
import { browserRepositoryUrl } from "./repository-url.ts";

type PersonResource = {
  kind: "person";
  account: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;
};
type AgentResource = {
  kind: "agent";
  account: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;
  model: string;
};
type RepositoryResource = {
  kind: "repository";
  project: string;
  projectName: string;
  name: string;
  url: string;
};
type Resource = PersonResource | AgentResource | RepositoryResource;

export function searchResourceCard(address: SearchResourceAddress): Card {
  return {
    key:
      address.kind === "repository"
        ? `search-repository:${address.project}:${address.name}`
        : `search-${address.kind}:${address.account}`,
    title:
      address.kind === "person"
        ? say("searchResource.person")
        : address.kind === "agent"
          ? say("searchResource.agent")
          : say("searchResource.repository"),
    render: () => <SearchResource address={address} />,
  };
}

function SearchResource({ address }: { address: SearchResourceAddress }) {
  const [resource, setResource] = useState<Resource | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    const asking: Promise<Resource | null> =
      address.kind === "repository"
        ? read<RepositoryResource | null>("search.repository", {
            project: address.project,
            name: address.name,
          })
        : address.kind === "person"
          ? read<PersonResource | null>("search.person", { account: address.account })
          : read<AgentResource | null>("search.agent", { account: address.account });
    void asking
      .then((found) => {
        if (live) setResource(found);
      })
      .catch(() => {
        if (live) setResource(null);
      });
    return () => {
      live = false;
    };
  }, [address]);

  if (resource === undefined) return <Spinner label={say("searchResource.loading")} />;
  if (resource === null) {
    return (
      <Text typeName="body" color="text.tertiary" testID="search-resource-unavailable">
        {say("searchResource.unavailable")}
      </Text>
    );
  }
  if (resource.kind === "repository") return <Repository resource={resource} />;
  return <Profile resource={resource} />;
}

function Profile({ resource }: { resource: PersonResource | AgentResource }) {
  const theme = useTheme();
  const face = useFace(resource.avatar);
  const name = calledBy(resource) ?? resource.handle;
  const [busy, setBusy] = useState(false);
  const [broke, setBroke] = useState(false);
  const start = async () => {
    setBusy(true);
    setBroke(false);
    try {
      const made = await act<{ id: string }>("conversation.create", {});
      await act("conversation.addParticipant", { conversation: made.id, account: resource.account });
      goTo(`/c/${made.id}`);
    } catch {
      setBroke(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Col style={{ gap: theme.space[4] }} testID={`search-resource-${resource.kind}`}>
      <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[3] }}>
        <Avatar name={name} src={face} size={48} />
        <Col style={{ flex: 1, minWidth: 0 }}>
          <Text typeName="bodySemibold" numberOfLines={1}>
            {name}
          </Text>
          <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
            {resource.kind === "agent" ? `@${resource.handle} · ${resource.model}` : `@${resource.handle}`}
          </Text>
        </Col>
      </Col>
      <Button onPress={() => void start()} loading={busy} disabled={busy}>
        {say("searchResource.startConversation")}
      </Button>
      {broke ? (
        <Text typeName="caption" color="danger">
          {say("searchResource.broke")}
        </Text>
      ) : null}
    </Col>
  );
}

function Repository({ resource }: { resource: RepositoryResource }) {
  const theme = useTheme();
  const destination = browserRepositoryUrl(resource.url);
  return (
    <Col style={{ gap: theme.space[4] }} testID="search-resource-repository">
      <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[3] }}>
        <Icon name="code" size={28} color="text.secondary" />
        <Col style={{ flex: 1, minWidth: 0 }}>
          <Text typeName="bodySemibold" numberOfLines={1}>
            {resource.name}
          </Text>
          <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
            {resource.projectName}
          </Text>
        </Col>
      </Col>
      <Text typeName="body" color="text.secondary" selectable>
        {resource.url}
      </Text>
      {destination ? (
        <Button onPress={() => window.open(destination, "_blank", "noopener,noreferrer")}>
          {say("searchResource.openRepository")}
        </Button>
      ) : null}
    </Col>
  );
}
